import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, Play, ShoppingCart, Star, Calendar } from 'lucide-react';
import { formatPrice } from '@/utils/format';
import { formatDistanceToNow } from 'date-fns';

interface SequenceGridProps {
  sequences: Array<{
    id: string;
    title: string;
    price: number;
    is_free: boolean;
    download_count: number;
    video_url: string | null;
    image_url: string | null;
    user_id: string;
    featured: boolean;
    created_at: string;
    software_type: string;
    holiday_type: string | null;
  }>;
}

export default function SequenceGrid({ sequences }: SequenceGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {sequences.map((sequence) => (
        <Link key={sequence.id} to={`/sequence/${sequence.id}`}>
          <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="p-0">
              <div className="relative aspect-video overflow-hidden rounded-t-lg bg-muted">
                {sequence.image_url ? (
                  <img
                    src={sequence.image_url}
                    alt={sequence.title}
                    className="w-full h-full object-cover"
                  />
                ) : sequence.video_url ? (
                  <div className="relative w-full h-full group">
                    <video
                      src={sequence.video_url}
                      className="w-full h-full object-cover"
                      muted
                      loop
                      onMouseEnter={(e) => e.currentTarget.play()}
                      onMouseLeave={(e) => e.currentTarget.pause()}
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Play className="h-12 w-12 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Download className="h-12 w-12 text-muted-foreground" />
                  </div>
                )}
                
                {sequence.featured && (
                  <Badge className="absolute top-2 right-2" variant="default">
                    Featured
                  </Badge>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="p-4 space-y-2">
              <h3 className="font-semibold line-clamp-2">{sequence.title}</h3>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Download className="h-3 w-3" />
                <span>{sequence.download_count} downloads</span>
              </div>

              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {sequence.software_type}
                </Badge>
                {sequence.holiday_type && (
                  <Badge variant="outline" className="text-xs">
                    {sequence.holiday_type}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>{formatDistanceToNow(new Date(sequence.created_at), { addSuffix: true })}</span>
              </div>
            </CardContent>
            
            <CardFooter className="p-4 pt-0 flex items-center justify-between">
              <div>
                {sequence.is_free ? (
                  <Badge variant="secondary" className="text-base">
                    Free
                  </Badge>
                ) : (
                  <span className="text-xl font-bold">
                    {formatPrice(sequence.price)}
                  </span>
                )}
              </div>
              
              <Button size="sm" onClick={(e) => e.preventDefault()}>
                <ShoppingCart className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </Link>
      ))}
    </div>
  );
}